import React from "react";
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react-dom";
import { createPortal } from "react-dom";
import { getReactPortalRoot } from "../../utils/reactPortalRoot.js";
import { joinClasses } from "./shared.js";

const ARROW_SIZE = 12;
const ARROW_OFFSET = ARROW_SIZE / 2;
const OPEN_DELAY_MS = 120;

const arrowBorderStylesByPlacement = {
  top: {
    borderRight: "0.5px solid rgba(0,0,0,0.10)",
    borderBottom: "0.5px solid rgba(0,0,0,0.10)",
  },
  bottom: {
    borderTop: "0.5px solid rgba(0,0,0,0.10)",
    borderLeft: "0.5px solid rgba(0,0,0,0.10)",
  },
  left: {
    borderTop: "0.5px solid rgba(0,0,0,0.10)",
    borderRight: "0.5px solid rgba(0,0,0,0.10)",
  },
  right: {
    borderBottom: "0.5px solid rgba(0,0,0,0.10)",
    borderLeft: "0.5px solid rgba(0,0,0,0.10)",
  },
};

const staticSideByPlacement = {
  top: "bottom",
  right: "left",
  bottom: "top",
  left: "right",
};

function getBasePlacement(value = "top") {
  return value.split("-")[0];
}

function targetIsInside(element, target) {
  if (!element || typeof Node === "undefined" || !(target instanceof Node)) {
    return false;
  }

  return element.contains(target);
}

export function Tooltip({
  content,
  title,
  trigger = "hover",
  placement = "top",
  maxWidth = 260,
  disabled = false,
  children,
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const openTimerRef = React.useRef(null);
  const animationFrameRef = React.useRef(null);
  const arrowRef = React.useRef(null);
  const tooltipId = React.useId();

  const { refs, floatingStyles, middlewareData, placement: resolvedPlacement } =
    useFloating({
      open: isOpen,
      placement,
      whileElementsMounted: autoUpdate,
      middleware: [
        offset(ARROW_OFFSET + 4),
        flip({ padding: 12 }),
        shift({ padding: 12 }),
        arrow({ element: arrowRef, padding: 10 }),
      ],
    });

  const clearOpenTimer = React.useCallback(() => {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const closeTooltip = React.useCallback(() => {
    clearOpenTimer();

    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    setIsVisible(false);
    setIsOpen(false);
  }, [clearOpenTimer]);

  const openTooltip = React.useCallback(() => {
    if (disabled || content == null) {
      return;
    }

    clearOpenTimer();
    setIsVisible(false);
    setIsOpen(true);
  }, [clearOpenTimer, content, disabled]);

  const scheduleOpen = React.useCallback(() => {
    if (disabled || content == null || isOpen) {
      return;
    }

    clearOpenTimer();
    openTimerRef.current = window.setTimeout(() => {
      openTimerRef.current = null;
      openTooltip();
    }, OPEN_DELAY_MS);
  }, [clearOpenTimer, content, disabled, isOpen, openTooltip]);

  React.useEffect(() => {
    if (!isOpen) {
      setIsVisible(false);
      return undefined;
    }

    animationFrameRef.current = window.requestAnimationFrame(() => {
      setIsVisible(true);
      animationFrameRef.current = null;
    });

    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen || trigger !== "click") {
      return undefined;
    }

    function handlePointerDown(event) {
      if (
        targetIsInside(refs.reference.current, event.target) ||
        targetIsInside(refs.floating.current, event.target)
      ) {
        return;
      }

      closeTooltip();
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        closeTooltip();
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeTooltip, isOpen, refs, trigger]);

  React.useEffect(() => {
    if (!disabled) {
      return undefined;
    }

    closeTooltip();
    return undefined;
  }, [closeTooltip, disabled]);

  React.useEffect(() => {
    return () => {
      clearOpenTimer();

      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [clearOpenTimer]);

  const basePlacement = getBasePlacement(resolvedPlacement);
  const arrowData = middlewareData.arrow || {};
  const arrowStyle = {
    left: arrowData.x != null ? `${arrowData.x}px` : "",
    top: arrowData.y != null ? `${arrowData.y}px` : "",
    right: "",
    bottom: "",
    [staticSideByPlacement[basePlacement]]: `-${ARROW_OFFSET}px`,
    ...arrowBorderStylesByPlacement[basePlacement],
  };

  const handleReferenceMouseEnter = trigger === "hover" ? scheduleOpen : undefined;
  const handleReferenceMouseLeave =
    trigger === "hover"
      ? (event) => {
          if (targetIsInside(refs.floating.current, event.relatedTarget)) {
            return;
          }

          closeTooltip();
        }
      : undefined;
  const handleReferenceFocus = trigger === "hover" ? scheduleOpen : undefined;
  const handleReferenceBlur =
    trigger === "hover"
      ? (event) => {
          if (targetIsInside(refs.floating.current, event.relatedTarget)) {
            return;
          }

          closeTooltip();
        }
      : undefined;
  const handleReferenceClick =
    trigger === "click"
      ? () => {
          if (disabled || content == null) {
            return;
          }

          if (isOpen) {
            closeTooltip();
            return;
          }

          openTooltip();
        }
      : undefined;
  const handleFloatingMouseEnter =
    trigger === "hover"
      ? () => {
          clearOpenTimer();
        }
      : undefined;
  const handleFloatingMouseLeave =
    trigger === "hover"
      ? (event) => {
          if (targetIsInside(refs.reference.current, event.relatedTarget)) {
            return;
          }

          closeTooltip();
        }
      : undefined;

  const tooltipNode =
    isOpen && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={refs.setFloating}
            role="tooltip"
            id={tooltipId}
            className={joinClasses([
              "pointer-events-auto rounded-[4px] border-[0.5px] border-[rgba(0,0,0,0.10)] bg-white px-3 py-2 text-[12px] leading-[1.55] text-floating-ink shadow-[0_4px_16px_rgba(0,0,0,0.10),0_1px_3px_rgba(0,0,0,0.06)] transition-opacity duration-80 ease-out z-9999",
              isVisible ? "opacity-100" : "opacity-0",
            ])}
            style={{
              ...floatingStyles,
              maxWidth: `min(${typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth}, calc(100vw - 24px))`,
            }}
            onMouseEnter={handleFloatingMouseEnter}
            onMouseLeave={handleFloatingMouseLeave}
          >
            <div
              ref={arrowRef}
              aria-hidden="true"
              className="absolute h-[12px] w-[12px] rotate-45 bg-white"
              style={arrowStyle}
            />
            {title ? (
              <div className="mb-1 border-b-[0.5px] border-[rgba(0,0,0,0.07)] pb-1">
                <div className="text-[12px] font-semibold leading-[1.45] text-floating-ink">
                  {title}
                </div>
              </div>
            ) : null}
            <div className="text-[12px] leading-[1.55] text-floating-ink">{content}</div>
          </div>,
          getReactPortalRoot(),
        )
      : null;

  return (
    <>
      <span
        ref={refs.setReference}
        className="inline-flex"
        aria-describedby={isOpen ? tooltipId : undefined}
        aria-expanded={trigger === "click" ? isOpen : undefined}
        onMouseEnter={handleReferenceMouseEnter}
        onMouseLeave={handleReferenceMouseLeave}
        onFocus={handleReferenceFocus}
        onBlur={handleReferenceBlur}
        onClick={handleReferenceClick}
      >
        {children}
      </span>
      {tooltipNode}
    </>
  );
}
